import React, { useState, useRef, useCallback, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";
import "./style.css";

const GAP = 8;
const EDGE = 8;
const ARROW_INSET = 12;

/** Nearest scrollable ancestor: the thread viewport the tooltip must stay inside of. */
function getScrollParentRect(node) {
  let el = node?.parentElement;
  while (el && el !== document.body) {
    const { overflowY, overflowX } = window.getComputedStyle(el);
    const scrollable = /(auto|scroll|overlay)/.test(`${overflowY}${overflowX}`);
    if (scrollable && el.scrollHeight > el.clientHeight) {
      return el.getBoundingClientRect();
    }
    el = el.parentElement;
  }
  return null;
}

const CustomTimeTooltip = ({ time, children }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const tipRef = useRef(null);

  const updatePosition = useCallback(() => {
    const anchor = wrapRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;

    const a = anchor.getBoundingClientRect();
    const width = tip.offsetWidth;
    const height = tip.offsetHeight;
    const scrollRect = getScrollParentRect(anchor);

    const topLimit = Math.max(scrollRect ? scrollRect.top : 0, 0);
    const bottomLimit = Math.min(
      scrollRect ? scrollRect.bottom : window.innerHeight,
      window.innerHeight
    );
    // Scrolled out of the thread viewport: keep it hidden instead of floating over the header.
    if (a.bottom <= topLimit || a.top >= bottomLimit) {
      setPos(null);
      return;
    }
    const leftLimit = Math.max(scrollRect ? scrollRect.left : 0, 0) + EDGE;
    const rightLimit =
      Math.min(scrollRect ? scrollRect.right : window.innerWidth, window.innerWidth) -
      width -
      EDGE;

    const below = a.top - topLimit < height + GAP;
    const anchorCenter = a.left + a.width / 2;
    const left = Math.max(leftLimit, Math.min(anchorCenter - width / 2, Math.max(rightLimit, leftLimit)));

    setPos({
      top: below ? a.bottom + GAP : a.top - height - GAP,
      left,
      arrowLeft: Math.max(ARROW_INSET, Math.min(anchorCenter - left, width - ARROW_INSET)),
      below,
    });
  }, []);

  useLayoutEffect(() => {
    if (!visible) {
      setPos(null);
      return;
    }
    updatePosition();
  }, [visible, time, updatePosition]);

  useEffect(() => {
    if (!visible) return undefined;
    const onViewportChange = () => updatePosition();
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange);
    return () => {
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange);
    };
  }, [visible, updatePosition]);

  return (
    <span
      ref={wrapRef}
      className="custom-time-tooltip-wrapper"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible &&
        createPortal(
          <span
            ref={tipRef}
            className={`custom-time-tooltip${pos?.below ? " custom-time-tooltip--below" : ""}`}
            style={{
              top: pos ? pos.top : 0,
              left: pos ? pos.left : 0,
              "--tooltip-arrow-left": `${pos ? pos.arrowLeft : 0}px`,
              visibility: pos ? "visible" : "hidden",
            }}
          >
            {time}
          </span>,
          document.body
        )}
    </span>
  );
};

export default CustomTimeTooltip;
